import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import PageHero from '../components/ui/PageHero';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { SectionHead } from '../components/ui/SectionHead';
import DlFacts from '../components/ui/DlFacts';
import Notice from '../components/ui/Notice';
import DataTable from '../components/ui/DataTable';
import Button from '../components/ui/Button';
import StatusChip from '../components/ui/StatusChip';
import { entity } from '../content/en';
import { organizationSchema, breadcrumbSchema } from '../components/ui/schema';

const About = () => {
  const { t } = useTranslation('cy');
  const meta = t('about.meta', { returnObjects: true });

  const schema = [
    organizationSchema(),
    breadcrumbSchema([{ label: 'Home', to: '/' }, { label: 'About', to: '/about' }]),
  ];

  const team = t('about.team.members', { returnObjects: true });

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/about" schema={schema} />

      <PageHero
        id="about-hero"
        eyebrow={t('about.hero.eyebrow')}
        title={t('about.hero.h1')}
        lead={t('about.hero.lead')}
        breadcrumb={<Breadcrumbs items={[{ label: t('nav.about') }]} />}
      >
        <p className="cy-body" style={{ marginTop: 24 }}>{t('about.hero.boundary')}</p>
        <p style={{ marginTop: 24 }}>
          <Link className="cy-link" to="#about-entity">{t('about.hero.cta')}</Link>
        </p>
      </PageHero>

      {/* about-entity */}
      <section className="cy-section cy-section--alt" id="about-entity">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('about.entity.h2')} lead={t('about.entity.lead')} />
          <DlFacts
            rows={t('about.entity.fields', { returnObjects: true }).map((f) => ({
              label: f.label,
              value: f.value,
              copyable: ['ABN', 'ACN', 'Phone', 'Email'].includes(f.label),
            }))}
          />
          {/* The register pointer existed in content but was never rendered — on the
              one page whose job is to be verifiable, it is the most useful line here. */}
          <p className="cy-fineprint" style={{ marginTop: 24 }}>{t('about.entity.register')}</p>
          <Notice variant="inline" style={{ marginTop: 16 }}>{t('about.entity.boundary')}</Notice>
        </div>
      </section>

      {/* about-story */}
      <section className="cy-section" id="about-story">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('about.story.h2')} />
          <p className="cy-body">{t('about.story.body')}</p>
          <p className="cy-fineprint" style={{ marginTop: 16 }}>{t('about.story.milestones')}</p>
        </div>
      </section>

      {/* about-principles */}
      <section className="cy-section cy-section--alt" id="about-principles">
        <div className="cy-container">
          <SectionHead eyebrow={t('about.principles.eyebrow')} title={t('about.principles.h2')} lead={t('about.principles.lead')} />
          <div className="cy-grid cy-grid--2">
            {t('about.principles.items', { returnObjects: true }).map((p, i) => (
              <div className="cy-principle" key={p.title} style={{ marginBottom: 8 }}>
                <h3 className="cy-principle__title">
                  <span className="cy-stage__num" style={{ fontSize: 16, marginRight: 8 }}>{String(i + 1).padStart(2, '0')}</span>
                  {p.title}
                </h3>
                <p className="cy-card-text">{p.body}</p>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 24 }}>
            <Link className="cy-link" to="/approach#approach-risk">{t('about.principles.cta')}</Link>
          </p>
        </div>
      </section>

      {/* about-team */}
      <section className="cy-section" id="about-team">
        <div className="cy-container">
          <SectionHead title={t('about.team.h2')} lead={t('about.team.lead')} />
          <div className="cy-grid cy-grid--2">
            {team.map((m) => (
              <div className="cy-card" key={m.name}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <h3 className="cy-card-title" style={{ margin: 0 }}>{m.name}</h3>
                  <StatusChip variant="pending">Pending</StatusChip>
                </div>
                <p className="cy-card-text" style={{ marginTop: 12 }}>{m.role}</p>
                <p className="cy-card-text">{m.history}</p>
                <p className="cy-card-text" style={{ marginTop: 8 }}>{m.verify}</p>
              </div>
            ))}
          </div>
          <p className="cy-fineprint" style={{ marginTop: 24 }}>{t('about.team.fallback')}</p>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/approach#approach-capabilities">{t('about.team.capabilitiesLink')}</Link>
          </p>
        </div>
      </section>

      {/* about-disclosure */}
      <section className="cy-section cy-section--alt" id="about-disclosure">
        <div className="cy-container">
          <SectionHead eyebrow={t('about.disclosure.eyebrow')} title={t('about.disclosure.h2')} lead={t('about.disclosure.lead')} />
          <DataTable
            columns={[
              { key: 'practice', label: 'What we do' },
              { key: 'detail', label: 'How it shows up on this site' },
            ]}
            rows={t('about.disclosure.rows', { returnObjects: true })}
          />
          <p className="cy-fineprint" style={{ marginTop: 24 }}>{t('about.disclosure.note')}</p>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/approach#approach-capabilities">{t('about.disclosure.cta')}</Link>
          </p>
        </div>
      </section>

      {/* about-relationships — rendered with boundary language only. The group
          affiliation sentence stays unpublished until U07 is confirmed (05 §8.2 E10);
          what is published here makes no affiliation claim, only states who carries
          contractual responsibility. */}
      <section className="cy-section" id="about-relationships">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('about.relationships.h2')} />
          <p className="cy-lede" style={{ marginTop: 0 }}>{t('about.relationships.contracting')}</p>
          <p className="cy-fineprint" style={{ marginTop: 24 }}>{t('about.relationships.note')}</p>
        </div>
      </section>

      {/* about-contact-cta */}
      <section className="cy-section cy-section--cta" id="about-contact-cta">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('about.contactCta.h2')} />
          <DlFacts
            rows={[
              { label: 'Phone', value: entity.phone, href: entity.phoneHref },
              { label: 'Email', value: entity.email, href: entity.emailHref },
              { label: 'Address', value: entity.address },
            ]}
          />
          <div style={{ marginTop: 32, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
            <Button to="/contact?topic=partnership">{t('about.contactCta.cta')}</Button>
            <Link className="cy-link" to="/submit-opportunity">{t('about.contactCta.submit')}</Link>
          </div>
          <p className="cy-fineprint" style={{ marginTop: 24 }}>
            <Link className="cy-link" to="/privacy">{t('about.contactCta.privacy')}</Link>
            {' · '}
            <Link className="cy-link" to="/terms">{t('about.contactCta.terms')}</Link>
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default About;
